import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from '../../../../config/TranslationProvider';
import {
    addHours,
    endOfToday,
    endOfTomorrow,
    formatISO,
    getHours,
    getMinutes,
    getSeconds,
    setDay,
    setHours,
    startOfToday,
    startOfTomorrow,
} from 'date-fns';
import DateRangePickerBox from './DateRangePickerBox';
import DateRangeInput from './DateRangeInput';

import './DateRangePicker.scss';
import { useAppConfiguration } from '../../../../AppConfiguration';

const isStartOrEndOfDay = date => (
    (getHours(date) === 0 && getMinutes(date) === 0 && getSeconds(date) === 0)
    || (getHours(date) === 23 && getMinutes(date) === 59 && getSeconds(date) === 59)
);

// Will be used to find out the selected type based on the passed start and end date and to set those values when
// selecting a different type.
// "date_range" is the fallback type when no other type matches and has therefore no values.
export const dateRangeTypes = Object.fromEntries(Object.entries({
    // 'type': [startDate, endDate]
    'arbitrary': [startOfToday(), ''], // Only show future events, '' means that no endDate is set
    'today': [startOfToday(), endOfToday()],
    'tomorrow': [startOfTomorrow(), endOfTomorrow()],
    // We start the weekend on friday 15:00
    'this_weekend': [setHours(setDay(startOfToday(), 5), 15), setDay(endOfToday(), 7)],
    'next_week': [setDay(startOfToday(), 8), setDay(endOfToday(), 14)],
    'date_range': null, // This is the fallback, it has no values
}).map(([key, startAndEndOrNull]) => {
    if (startAndEndOrNull === null) {
        return [key, startAndEndOrNull];
    }

    let [from, to] = startAndEndOrNull;

    // If from or to is set to the start or end of the day, set the time to 6am instead, as we sometimes have events
    // that go on until deep into the night, and we don't want to show an event that already ended at 5am today as a
    // "today event"
    if (from && isStartOrEndOfDay(from)) {
        from = addHours(from, 6);
    }
    if (to && isStartOrEndOfDay(to)) {
        to = addHours(to, 6);
    }

    return [key, [from, to]];
}));

// Format all dates as ISO
for (const key in dateRangeTypes) {
    if (dateRangeTypes[key] !== null) {
        dateRangeTypes[key] = dateRangeTypes[key].map(value => value === '' ? value : formatISO(value));
    }
}

const DateRangePicker = ({register, selectedStartDate, selectedEndDate, inline}) => {
    const translations = useTranslation('date_range_picker');
    const closeDateRangeTimeOverlay = useTranslation('date_range_time_close');

    const selectRef = React.createRef();
    const dialogRef = React.createRef();

    const { filterConfiguration } = useAppConfiguration();

    // Check if the app-configuration has another default defined
    let defaultDateType = filterConfiguration.dateOption?.default || 'arbitrary';
    defaultDateType = defaultDateType in dateRangeTypes ? defaultDateType : 'arbitrary';

    const [selectedDateType, setSelectedDateType] = useState(null);

    const [startDate, setStartDate] = useState(
        selectedStartDate || dateRangeTypes[defaultDateType][0]
    );

    const [endDate, setEndDate] = useState(
        selectedEndDate || dateRangeTypes[defaultDateType][1]
    );

    useEffect(() => {
        if (selectedDateType) {
            return;
        }

        // Use the default date defined by the configuration if no date is selected
        if (!selectedStartDate && !selectedEndDate) {
            setSelectedDateType(defaultDateType);
            return;
        }

        // Find a matching date-type based on the start- and end-date
        for (const type in dateRangeTypes) {
            const values = dateRangeTypes[type];

            if (values === null) {
                // Skip types without any values to compare against
                continue;
            }

            if (startDate === values[0] && endDate === values[1]) {
                setSelectedDateType(type);
                return;
            }
        }

        // No fitting date-type found, use the date_range type
        setSelectedDateType(defaultDateType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Show date range picker on mount?
    const [showDateRangePicker, setShowDateRangePicker] = useState(selectedDateType === 'date_range');
    const [showDateRangePickerOverlay, setShowDateRangePickerOverlay] = useState(selectedDateType === 'date_range');

    function onChangeDateType(selectedType) {
        setShowDateRangePicker(selectedType === 'date_range');
        setShowDateRangePickerOverlay(selectedType === 'date_range');

        const dateValues = dateRangeTypes[selectedType];

        if (dateValues !== null) {
            setStartDate(dateValues[0]);
            setEndDate(dateValues[1]);
        }

        setSelectedDateType(selectedType);
    }

    // Toggle the date-picker dialog
    useEffect(() => {
        const handleBodyScroll = (isOpen) => {
            document.body.style.overflow = isOpen ? 'hidden' : '';
        };

        if (!dialogRef.current) return;

        if (showDateRangePickerOverlay) {
            handleBodyScroll(true);
            dialogRef.current.showModal();
        } else {
            handleBodyScroll(false);
            dialogRef.current.close();
        }

        return () => {
            handleBodyScroll(false);
        };
    }, [showDateRangePickerOverlay, dialogRef]);

    function handleDatePickerBackdropClick(event) {
        if (!dialogRef.current) return;

        const rect = dialogRef.current.getBoundingClientRect();
        const isInDialog =
            rect.top <= event.clientY &&
            event.clientY <= rect.top + rect.height &&
            rect.left <= event.clientX &&
            event.clientX <= rect.left + rect.width;

        if (!isInDialog) {
            setShowDateRangePickerOverlay(false);
        }
    }

    return (
        <div className={inline ? 'single_search_field date-wrapper' : 'form-group'}>
            {/* We want events ending after the start date and starting before the end date */}
            <input ref={register} type="hidden" name="end_date_from" value={startDate} aria-label={useTranslation('enter_start_date')} />
            <input ref={register} type="hidden" name="start_date_to" value={endDate} aria-label={useTranslation('enter_end_date')} />

            {showDateRangePickerOverlay && (
                <dialog
                    ref={dialogRef}
                    aria-modal
                    id="date-range-picker-dialog"
                    onClose={() => setShowDateRangePickerOverlay(false)}
                    onClick={handleDatePickerBackdropClick}
                >
                    <button
                        type="button"
                        className="close-picker-btn"
                        onClick={() => setShowDateRangePickerOverlay(false)}
                        aria-label={closeDateRangeTimeOverlay}
                    >
                        <i className="la la-times" />
                    </button>

                    <DateRangePickerBox
                        startDate={startDate}
                        inline={inline}
                        endDate={endDate || startDate}
                        translations={translations}
                        onChange={(startDate, endDate) => {
                            setStartDate(startDate);
                            setEndDate(endDate);
                            setShowDateRangePickerOverlay(false);
                        }}
                    />
                </dialog>
            )}

            <div className={classnames({
                'date-range-picker': true,
                'date-range-input-visible': showDateRangePicker,
            })}>
                <div className="select-basic date-range-type">
                    <select
                        className="form-control"
                        value={selectedDateType}
                        onChange={event => onChangeDateType(event.target.value)}
                        aria-label={useTranslation('date_range_label')}
                        ref={selectRef}
                    >
                        {Object.keys(dateRangeTypes).map(type => (
                            <option key={type} value={type}>
                                {/* eslint-disable-next-line react-hooks/rules-of-hooks */}
                                {useTranslation(type)}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="date-range-input">
                    <DateRangeInput
                        startDate={startDate}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                        endDate={endDate || startDate}
                        onSpace={() => setShowDateRangePickerOverlay(true)}
                        onClick={() => setShowDateRangePickerOverlay(true)}
                        inline={inline}
                        isHidden={selectedDateType !== 'date_range'}
                    />
                </div>
            </div>
        </div>
    );
};

export default DateRangePicker;
